<template>
  <div>
    <v-card>
      <v-card-title>
        <div>
          <div class="headline">Virksomheder</div>
          <!--<span class="grey&#45;&#45;text">Oversigt over din daglige præstation de seneste 30 dage</span>-->
        </div>
        <v-spacer></v-spacer>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              outlined
              color="primary"
              class="mr-3"
              @click="showFavoritesOnly = !showFavoritesOnly"
            >
              <v-icon small :color="showFavoritesOnly ? 'amber darken-2' : undefined">
                {{ showFavoritesOnly ? 'fas fa-star' : 'fal fa-star' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ showFavoritesOnly ? 'Vis alle' : 'Vis favoritter' }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              outlined
              color="primary"
              class="mr-3"
              :loading="exporting"
              :disabled="exporting"
              @click="exportBusinesses"
            >
              <v-icon small>fal fa-file-csv</v-icon>
            </v-btn>
          </template>
          <span>Download CSV (aktive virksomheder)</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              color="primary"
              @click="showFiltersDialog = true"
              :v-on="on"
            >
              <v-badge color="blue-grey lighten-2">
                <span slot="badge" v-if="filtersCount > 0">{{
                  filtersCount
                }}</span>
                <v-icon small>fas fa-filter</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>Filtrér</span>
        </v-tooltip>
      </v-card-title>
      <v-card-text>
        <business-data-table
          :items="displayedBusinesses"
          :favorite-uuids="favoriteUuids"
          @toggle-favorite="handleToggleFavorite"
          :loading="loading"
          :total="total"
          :page="page"
          @clicked="
            $router.push({ name: 'business', params: { id: $event.uuid } })
          "
          @changePage="getBusinessesData($event)"
        ></business-data-table>
      </v-card-text>
    </v-card>

    <business-filters-dialog
      v-model="showFiltersDialog"
      :filters="filters"
      @save="(filters = $event), (page = 1)"
    ></business-filters-dialog>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import search_filters_url from "@/helpers/mixins/search_filters_url.js";
import BusinessDataTable from "../data-tables/BusinessDataTable";
import BusinessFiltersDialog from "../dialogs/BusinessFiltersDialog";
import { mapActions } from "vuex";

export default {
  components: { BusinessFiltersDialog, BusinessDataTable },
  mixins: [search_filters_url],
  data: () => ({
    favoriteUuids: [],
    showFavoritesOnly: false,
    loading: true,
    exporting: false,
    businesses: [],
    showFiltersDialog: false,
    filters: {},
    total: null,
    page: 1
  }),
  computed: {
    displayedBusinesses() {
      return this.businesses;
    },
    filtersCount() {
      let filters = this.cloneDeep(this.filters);

      let count = Object.keys(filters).length;
      // If search key is set but has no value. Then subtract 1 from count
      if ("search" in filters && !filters.search) {
        count--;
      }
      return count;
    }
  },
  watch: {
    filters: {
      handler: function() {
        this.businesses = [];
        this.getBusinessesData(this.page);
      },
      deep: true
    },
    
    showFavoritesOnly() {
      this.getBusinessesData(1);
    }
  },
  methods: {
    ...mapActions("core", ["getBusinesses"]),
    ...mapActions("moderator", ["getModeratorFavorites", "toggleModeratorFavorite"]),

    async handleToggleFavorite(uuid) {
      // Optimistic update — toggle immediately
      const index = this.favoriteUuids.indexOf(uuid);
      if (index === -1) {
        this.favoriteUuids.push(uuid);
      } else {
        this.favoriteUuids.splice(index, 1);
      }

      try {
        const result = await this.toggleModeratorFavorite({ type: 'business', uuid });
        // Reconcile with server truth
        this.favoriteUuids = result.business_uuids;
        
        
        if (this.showFavoritesOnly) {
          this.getBusinessesData(1);
        }
        
      } catch (e) {
        // Revert on error
        if (index === -1) {
          this.favoriteUuids = this.favoriteUuids.filter(u => u !== uuid);
        } else {
          this.favoriteUuids.splice(index, 0, uuid);
        }
      }
    },

    async exportBusinesses() {
      this.exporting = true;
      const baseUrl = `${process.env.VUE_APP_API_URL}moderator/businesses/export`;

      try {
        const response = await fetch(baseUrl, {
          method: 'GET',
          headers: {
            'Authorization': "Bearer " + this.$authHelper.getAccessToken(),
          },
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const blob = await response.blob();
        saveAs(blob, 'make_influence_businesses.csv');

      } catch (error) {
        console.error('Business export failed:', error);
      } finally {
        this.exporting = false;
      }
    },

    getBusinessesData(page = 1) {
      this.businesses = [];
      this.page = page;
      this.loading = true;
      this.setUrlSearchFilters({ ...this.filters, page: this.page });

      if (this.showFavoritesOnly) {
        if (this.favoriteUuids.length === 0) { 
          this.total = 1;
          this.loading = false;
          return;
        }
        const params = {
          ...this.filters,
          uuids: this.favoriteUuids, 
          limit: 15, 
          page: this.page
        };
        this.getBusinesses(params).then(businesses => {
          this.businesses = businesses.response;
          this.total = businesses.lastPage; 
          this.loading = false;
        });
        return; 
      }

      const params = {
        ...this.filters,
        page: this.page,
        limit: 15,
        pinned_uuids: this.favoriteUuids,
      };
      this.getBusinesses(params).then(businesses => {
        this.businesses = businesses.response;
        this.total = businesses.lastPage;
        this.loading = false;
      });
    }
  },
  async created() {
    this.filters = this.getUrlSearchFilters();
    const restoredPage = parseInt(this.filters.page, 10);
    delete this.filters.page;

    if (Object.keys(this.filters).length === 0) {
      this.filters.has_subscription = true;
    }

    this.page = restoredPage > 0 ? restoredPage : 1;

    const favorites = await this.getModeratorFavorites();
    this.favoriteUuids = favorites.business_uuids;
    this.getBusinessesData(this.page); 
  }
};
</script>